import { warning, critical } from '../Notifications/notice';
import { dataObjectToCsvByKeys } from '../../utils/csvKeys';
import { endpointRequest } from '../EndpointService/EndpointFunctions';

//TODO: Due to slowness of some queries and new filtering system we must be able to cancel ongoing queries or atleast not to implement their answers to the current dataset

export default function createAlertsService(jokiInstance) {
    const serviceId = 'AlertsService';
    const joki = jokiInstance;

    const alerts = new Map();

    const requestsInProgress = {};

    let pinger = null;

    function messageHandler(event) {
        switch (event.key) {
            case 'getState':
                return Array.from(alerts.values());
            case 'getAlerts':
                return getAll();
            case 'getActiveAlerts':
                return getActiveAlerts();
            case 'getAlert':
                return get(event.body);
            case 'closeAlert':
                return closeAlert(event.body);
            case 'createTestAlert':
                return createTestAlert(event.body.device);
            case 'AuthenticationStatusChanged':
                if (event.from === 'AuthenticationService') {
                    authStatusChange(event.body);
                }
                break;
            case 'sessionHasDied':
                if (event.from === 'AuthenticationService') {
                    clearInterval(pinger);
                    pinger = null;
                }
                break;
            case 'DataResetRequired':
                resetData();
                break;
            case 'QueryFiltersChanged':
                resetAlarts();
                break;
            default:
                break;
        }
    }

    function authStatusChange(changedTo) {
        if (pinger === null && changedTo === 'USER_AUTHENTICATED') {
            pinger = setInterval(reloader, process.env.REACT_APP_LIFE_DATA_REFRESH_TIME);
        }

        if (pinger !== null && changedTo !== 'USER_AUTHENTICATED') {
            clearInterval(pinger);
            pinger = null;
            alerts.clear();
        }
    }

    function resetData() {
        alerts.clear();
        getAll();
    }

    async function reloader() {
        try {
            const results = await askEndpoint('alertList_simple', { state: 'ACTIVE' });
            const alertComparison = results.alerts.reduce(
                (acc, cur) => {
                    const oldAlert = alerts.get(cur.id);
                    if (oldAlert === undefined) {
                        acc.newAlerts = acc.newAlerts + 1;
                        acc.newIds.push(cur.device_Code);
                    } else {
                        if (cur.triggerCounter > oldAlert.triggerCounter || cur.lastTimeSeen !== oldAlert.lastTimeSeen) {
                            acc.updatedAlerts = acc.updatedAlerts + 1;
                            acc.updatedIds.push(cur.device_Code);
                        }
                    }

                    return acc;
                },
                { newAlerts: 0, updatedAlerts: 0, newIds: [], updatedIds: [] }
            );

            if (alertComparison.newAlerts > 0) {
                critical(`${alertComparison.newAlerts} new alerts in devices\n \n${alertComparison.newIds.join('\n')}`);
            }
            if (alertComparison.updatedAlerts > 0) {
                warning(`${alertComparison.updatedAlerts} alerts had been triggered again in devices\n \n${alertComparison.updatedIds.join('\n')}`);
            }
            if (alertComparison.newAlerts > 0 || alertComparison.updatedAlerts > 0) {
                fetchAlerts();
            }
        } catch (err) {
            console.error(`${serviceId}:reloader: Could not reload`, err);
            if (err === true) {
                return;
            }
            if (Array.isArray(err) && err[0] !== undefined) {
                critical(`IPA API ERROR: \n ${err[0].message}`);
            } else {
                critical(`IPA API ERROR: \n ${err}`);
            }
        }
    }

    function get(alertId) {}

    function getAll() {
        fetchAlerts();
        return new Map(alerts);
    }

    function getActiveAlerts() {
        fetchActiveAlerts();
        return new Map(alerts);
    }

    function resetAlarts() {
        alerts.clear();
        cancelRequest();
        getAll();
    }

    async function closeAlert(data) {
        const { alert, device } = data;
        // console.log("CANCEL ALERT!", alert);

        const convertedAlertData = {
            deviceId: alert.device_Id,
            deviceCode: device.code,
            deviceType: device.type,
            alarmState: 'OFF',
            eventType: 'Alert',
            source: alert.source,
            alarmType: alert.type,
            severity: alert.severity,
        };

        // console.log("Converted alert data", convertedAlertData);
        const alarmData = dataObjectToCsvByKeys(convertedAlertData, [
            'deviceId',
            'deviceCode',
            'deviceType',
            'alarmState',
            'eventType',
            'source',
            'alarmType',
            'severity',
        ]);

        try {
            await endpointRequest(joki, 'alert_cancel', {
                filters: {
                    activityType: 'DeviceStatus_NOTRACK',
                    fields_csv: alarmData.header,
                },
                body: alarmData.body[0],
            });
            setTimeout(() => {
                fetchAlerts();
                joki.trigger({
                    to: 'DevicesService',
                    key: 'forceDeviceUpdate',
                    body: {
                        code: device.code,
                    },
                });
            }, 1000);
        } catch (err) {
            console.warn(`${serviceId}:closeAlert: Endpoint Error`, err);
        }
    }

    /**
     * Creates a new test alert
     *
     * @param {object} device
     */
    async function createTestAlert(device) {
        // console.log('Create test alert for ', device);

        const convertedAlertData = {
            deviceId: device.id,
            deviceCode: device.code,
            deviceType: device.type,
            alarmState: 'ON',
            eventType: 'Alert',
            source: 'AdminPortal',
            alarmType: 'testAlert',
            severity: 0.8,
            alarmPayload: { message: 'This is a test alert generated by admin portal to test alert system.' },
        };

        const alarmData = dataObjectToCsvByKeys(convertedAlertData, [
            'deviceId',
            'deviceCode',
            'deviceType',
            'alarmState',
            'eventType',
            'source',
            'alarmType',
            'severity',
            'alarmPayload',
        ]);

        //console.log(alarmData);

        try {
            await endpointRequest(joki, 'alert_create', {
                filters: {
                    fields_csv: alarmData.header,
                },
                body: alarmData.body[0],
            });
            setTimeout(() => {
                fetchAlerts();
                joki.trigger({
                    to: 'DevicesService',
                    key: 'forceDeviceUpdate',
                    body: {
                        code: device.code,
                    },
                });
            }, 1000);
        } catch (err) {
            console.warn(`${serviceId}:createAlert: Endpoint Error`, err);
        }
    }

    async function fetchActiveAlerts() {
        async function fetchIds() {
            try {
                const results = await askEndpoint('alertList_simple', { state: 'ACTIVE' });
                mergeResultsToCurrent(results.alerts);
                // console.log("RES 1", results, "\n\n\n\n\n\n");
                return true;
            } catch (err) {
                return false;
            }
        }

        async function fetchFullActiveAlerts() {
            try {
                const results = await askEndpoint('alertList_full', { state: 'ACTIVE' });
                mergeResultsToCurrent(results.alerts);
                // console.log("RES 2", results, "\n\n\n\n\n\n");
            } catch (err) {
                if (err !== 'Cancelled') {
                    console.error('AlertsService:fetchAlerts:', err);
                }
            }
        }

        triggerLoadingEvent();
        if (alerts.size === 0) {
            if (await fetchIds()) {
                await fetchFullActiveAlerts();
            }
        } else {
            await fetchFullActiveAlerts();
        }
        triggerUpdatedEvent();
    }

    async function fetchAlerts() {
        triggerLoadingEvent();
        try {
            const results = await askEndpoint('alertList_full', { state: 'ACTIVE' });
            if (results === undefined) {
                throw new Error(`${serviceId}:fetchAlerts: endpoint 'alertList_full' returned undefined.`);
            }
            mergeResultsToCurrent(results.alerts);
        } catch (err) {
            if (err.name !== 'AbortError' && err.name !== 'EndpointQueueError' && err.name !== 'RequestWarning') {
                console.error(`${serviceId}:fetchAlerts`, err);
            }
        }
        triggerUpdatedEvent();
    }

    function askEndpoint(endpointId, requestFilters = {}) {
        return new Promise(async (resolve, reject) => {
            const reqFilterStr = `${endpointId}?${Object.keys(requestFilters).reduce((str, k) => {
                str += `${k}:${requestFilters[k]},`;
                return str;
            }, '')}`;
            // console.log(`\n\n\n\n${reqFilterStr}\n\n\n\n\n\n\n\n\n`);

            if (requestsInProgress[reqFilterStr] === true) {
                reject({ name: 'RequestWarning', message: 'Identical request is already in progress' });
            }

            requestsInProgress[reqFilterStr] = true;

            try {
                const results = await endpointRequest(joki, endpointId, { filters: requestFilters });
                if (requestsInProgress[reqFilterStr] === 'CANCELLED') {
                    requestsInProgress[reqFilterStr] = false;
                    reject('Cancelled');
                } else {
                    requestsInProgress[reqFilterStr] = false;
                    resolve(results);
                }
            } catch (err) {
                requestsInProgress[reqFilterStr] = false;
                reject(err);
            }
        });
    }

    function cancelRequest(queryString = null) {
        // Cancel all requests
        if (queryString === null) {
            Object.keys(requestsInProgress).forEach(req => {
                if (requestsInProgress[req] === true) {
                    requestsInProgress[req] = 'CANCELLED';
                }
            });
            return;
        }
        if (requestsInProgress[queryString] === true) {
            requestsInProgress[queryString] = 'CANCELLED';
        }
    }

    function mergeResultsToCurrent(results) {
        if (Array.isArray(results)) {
            results.forEach(alert => {
                if (alert.device) {
                    alert.device_name = alert.device.name;
                    alert.device_type = alert.device.type;
                    alert.description = alert.description || '';
                }
                if (alerts.has(alert.id)) {
                    alerts.set(alert.id, Object.assign({}, alerts.get(alert.id), alert));
                } else {
                    alerts.set(alert.id, alert);
                }
            });
        }
    }

    function triggerUpdatedEvent() {
        joki.serviceUpdate(serviceId, Array.from(alerts.values()), { serviceUpdate: true });
    }

    function triggerLoadingEvent() {
        joki.serviceUpdate(serviceId, 'LOADING', { serviceUpdate: true });
    }

    joki.addService({
        id: serviceId,
        fn: messageHandler,
    });
}
